import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PaginationBar from '../../../layout/PaginationBar';
import CompletedRecallItem from '../CompletedRecallItem';
import { CompletedRecallTour } from '../../../../types';
import { determineIsLoadingFromState, getOrdersFromState, selectPickerStore } from '../../../../redux/selectors';
import { getCompletedRecalls } from '../../../../redux/actions/orders.actions';
import { RECALL_HISTORY_DAYS } from '../../../../constants';
import styles from './RecallHistory.css';

type RecallsHistoryProps = {
  selectedCompletedRecallTour: CompletedRecallTour | null;
  setSelectedCompletedRecallTour: (tour: CompletedRecallTour | null) => void;
};

const RecallHistory: React.FC<RecallsHistoryProps> = ({
  selectedCompletedRecallTour,
  setSelectedCompletedRecallTour,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const translationRoot = 'ORDER_LIST';
  const isLoading = useSelector(determineIsLoadingFromState);
  const ordersState = useSelector(getOrdersFromState);
  const pickerStore = useSelector(selectPickerStore);

  const { completedRecalls } = ordersState;
  const hasRecalls = completedRecalls && completedRecalls.content.length > 0;
  const isRecallTourSelected = selectedCompletedRecallTour !== null;

  if (isLoading || !completedRecalls) return null;

  if (!hasRecalls && !isRecallTourSelected) {
    return (
      <article className={styles.noOrdersContainer}>
        <p>{t(`${translationRoot}.NO_RECALL_ORDERS`)}</p>
      </article>
    );
  }

  const recallsPageChange = (pageNumber: number) => {
    dispatch(
      getCompletedRecalls({
        fulfillmentStoreID: pickerStore.storeID || '',
        daysAgo: RECALL_HISTORY_DAYS,
        pageNumber: pageNumber - 1,
      })
    );
  };

  return (
    <>
      <ul className={isRecallTourSelected ? styles.listSelected : styles.listNotSelected}>
        {completedRecalls.content
          .sort((a: CompletedRecallTour, b: CompletedRecallTour) => {
            const dateA = new Date(a.completedDate);
            const dateB = new Date(b.completedDate);
            return dateB.getTime() - dateA.getTime();
          })
          .map((completedRecall: CompletedRecallTour) => {
            const isSelected = selectedCompletedRecallTour?.recallPickTourId === completedRecall.recallPickTourId;

            if (selectedCompletedRecallTour && !isSelected) return null;

            return (
              <CompletedRecallItem
                key={completedRecall.recallPickTourId}
                isSelected={isSelected}
                completedRecall={completedRecall}
                onSelect={() => {
                  setSelectedCompletedRecallTour(isSelected ? null : completedRecall);
                }}
              />
            );
          })}
        {!isRecallTourSelected && (
          <li style={{ marginTop: 'auto' }}>
            <PaginationBar
              isDisabled={false}
              currentPage={completedRecalls.page.number + 1}
              totalPages={completedRecalls.page.totalPages}
              onPageChange={recallsPageChange}
            />
          </li>
        )}
      </ul>
    </>
  );
};

export default RecallHistory;
